import { useEffect, useMemo, useRef } from 'react';
import CollectVariableTreeNode from '../types/BotBuilder/CollectVariableTreeNode';
import BotResponseTreeNode from '../types/BotBuilder/BotResponseTreeNode';
import CollectVariableNodeData from '../types/BotBuilder/CollectVariableNodeData';
import IntentSlot from '../types/BotBuilder/IntentSlot';
import Intent from '../types/BotBuilder/Intent';

interface UseSlotSyncProps {
  collectVariableNode: CollectVariableTreeNode | null;
  intentSlotMap: Record<string, any>;
  intent: Intent | null;
  onNodeUpdate: (node: BotResponseTreeNode) => void;
  data: CollectVariableNodeData;
}

function useSlotSync({
  collectVariableNode,
  intentSlotMap,
  intent,
  onNodeUpdate,
  data
}: UseSlotSyncProps): IntentSlot {
  const lastIntentSlotIdRef = useRef<number | null>(null);

  const currentSlot = useMemo(() => {
    if (!collectVariableNode?.slotName) return null;
    return intentSlotMap[collectVariableNode.slotName] ?? null;
  }, [collectVariableNode?.slotName, intentSlotMap]);

  useEffect(() => {
    if (!collectVariableNode) return;

    if (
      currentSlot &&
      currentSlot.intentSlotId !== lastIntentSlotIdRef.current &&
      collectVariableNode.intentSlotId !== currentSlot.intentSlotId
    ) {
      Object.assign(collectVariableNode, {
        intentSlotId: currentSlot.intentSlotId,
        slotType: currentSlot.slotName
      });
      onNodeUpdate(data.node);
      lastIntentSlotIdRef.current = currentSlot.intentSlotId;
    } else if (
      !collectVariableNode.slotType &&
      collectVariableNode.slotName &&
      intent
    ) {
      const intentSlot = intent.slots.find(
        (islot: any) => islot.slotName === collectVariableNode.slotName
      );
      if (intentSlot && intentSlot.id !== lastIntentSlotIdRef.current) {
        Object.assign(collectVariableNode, {
          slotType: intentSlot.slotType,
          intentSlotId: intentSlot.id
        });
        onNodeUpdate(data.node);
        lastIntentSlotIdRef.current = intentSlot.id;
      }
    }
  }, [collectVariableNode, currentSlot, intent, onNodeUpdate, data.node]);

  return currentSlot;
}

export default useSlotSync;
